import type React from 'react';
import { useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  AlertCircle,
  Inbox,
  Footprints,
  Gauge,
  CalendarRange,
  Timer,
  Loader2,
} from 'lucide-react';
import { usePlayerCrossStats } from '@/hooks/usePlayerCrossStats';
import { computeTrend } from '@/lib/playerDashboard';
import { msToKmh } from '@/lib/utils';
import CrossKpiTiles from './CrossKpiTiles';
import EvolutionCard from './EvolutionCard';
import CourtZoneCard from './CourtZoneCard';
import CrossShotDistributionCard from './CrossShotDistributionCard';
import type { PerSessionEntry } from '@/types/playerCrossStats';

const PlayerReportPage: React.FC = () => {
  const { t } = useTranslation(['common', 'report']);
  const { userId = 'local' } = useParams<{ userId: string }>();
  const { data, loading, error } = usePlayerCrossStats(userId);

  if (loading) {
    return (
      <div className="flex min-h-[240px] items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="flex min-h-[240px] flex-col items-center justify-center gap-3">
        <AlertCircle className="h-8 w-8 text-slate-400" />
        <p className="text-slate-500">{t('common:somethingWentWrong')}</p>
      </div>
    );
  }

  return <PlayerDashboard data={data} />;
};

const PlayerDashboard: React.FC<{
  data: NonNullable<ReturnType<typeof usePlayerCrossStats>['data']>;
}> = ({ data }) => {
  const { t, i18n } = useTranslation(['report']);

  const dateFmt = useMemo(
    () =>
      new Intl.DateTimeFormat(i18n.language, {
        month: 'short',
        day: 'numeric',
      }),
    [i18n.language],
  );

  const dated = useMemo(
    () =>
      sortedByDate(data.per_session).filter(
        (s): s is PerSessionEntry & { played_at: string } => !!s.played_at,
      ),
    [data.per_session],
  );

  const datedWithMeta = useMemo(
    () =>
      dated.map((s) => ({
        session: s,
        x: new Date(s.played_at).getTime(),
        label: dateFmt.format(new Date(s.played_at)),
      })),
    [dated, dateFmt],
  );

  const distancePoints = useMemo(
    () =>
      datedWithMeta.map(({ session, x, label }) => ({
        x,
        y: session.distance_m,
        label,
      })),
    [datedWithMeta],
  );

  const speedPoints = useMemo(
    () =>
      datedWithMeta.map(({ session, x, label }) => ({
        x,
        y: msToKmh(session.mean_velocity_ms),
        label,
      })),
    [datedWithMeta],
  );

  const durationPoints = useMemo(
    () =>
      datedWithMeta.map(({ session, x, label }) => ({
        x,
        y: session.game_time_s / 60,
        label,
      })),
    [datedWithMeta],
  );

  const sessionsPoints = useMemo(
    () =>
      datedWithMeta.map(({ x, label }, i) => ({
        x,
        y: i + 1,
        label,
      })),
    [datedWithMeta],
  );

  const xFormatter = (v: number) => dateFmt.format(new Date(v));

  const distanceUnit = data.totals.distance_m >= 1000 ? 'km' : 'm';
  const distanceYFormatter = (v: number) =>
    distanceUnit === 'km' ? `${(v / 1000).toFixed(1)}` : `${Math.round(v)}`;
  const distanceCurrent =
    distancePoints.length > 0
      ? formatDistance(distancePoints[distancePoints.length - 1].y)
      : '—';

  const speedCurrent =
    speedPoints.length > 0
      ? `${speedPoints[speedPoints.length - 1].y.toFixed(1)} km/h`
      : '—';

  const durationCurrent =
    durationPoints.length > 0
      ? `${Math.round(durationPoints[durationPoints.length - 1].y)} min`
      : '—';

  if (data.sessions_count === 0) {
    return (
      <div className="mx-auto flex min-h-[240px] w-full max-w-md flex-col items-center justify-center gap-3 px-4">
        <Inbox className="h-10 w-10 text-slate-400" />
        <p className="text-center text-slate-500">
          {t('report:playerReport.empty')}
        </p>
      </div>
    );
  }

  return (
    <div className="mx-auto flex w-full max-w-6xl flex-col gap-4 px-4 py-6">
      <CrossKpiTiles
        totalDistanceM={data.totals.distance_m}
        totalShots={data.totals.shots}
        sessionsCount={data.sessions_count}
        avgSpeedMs={data.averages.mean_velocity_ms}
        maxSpeedMs={data.averages.max_velocity_ms}
        avgDistancePerSessionM={data.averages.distance_per_session_m}
        avgShotsPerSession={data.averages.shots_per_session}
        perSession={dated}
        delay={0}
      />

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <EvolutionCard
          title={t('report:playerReport.distanceOverTime')}
          subtitle={t('report:playerReport.distanceOverTimeSubtitle')}
          icon={<Footprints className="h-5 w-5 text-white" />}
          points={distancePoints}
          accent="sky"
          currentLabel={distanceCurrent}
          trend={computeTrend(distancePoints.map((p) => p.y))}
          yFormatter={distanceYFormatter}
          xFormatter={xFormatter}
          emptyText={t('report:playerReport.noDatedSessions')}
          delay={100}
        />
        <EvolutionCard
          title={t('report:playerReport.speedOverTime')}
          subtitle={t('report:playerReport.speedOverTimeSubtitle')}
          icon={<Gauge className="h-5 w-5 text-white" />}
          points={speedPoints}
          accent="amber"
          currentLabel={speedCurrent}
          trend={computeTrend(speedPoints.map((p) => p.y))}
          yFormatter={(v) => v.toFixed(1)}
          xFormatter={xFormatter}
          emptyText={t('report:playerReport.noDatedSessions')}
          delay={200}
        />
        <EvolutionCard
          title={t('report:playerReport.sessionsOverTime')}
          subtitle={t('report:playerReport.sessionsOverTimeSubtitle')}
          icon={<CalendarRange className="h-5 w-5 text-white" />}
          points={sessionsPoints}
          accent="emerald"
          // Match the headline to the chart's last cumulative value rather
          // than `data.sessions_count`, which also includes dateless rows
          // that don't appear on the chart.
          currentLabel={String(
            sessionsPoints.length > 0
              ? sessionsPoints[sessionsPoints.length - 1].y
              : 0,
          )}
          trend={null}
          yFormatter={(v) => Math.round(v).toString()}
          xFormatter={xFormatter}
          emptyText={t('report:playerReport.noDatedSessions')}
          delay={300}
        />
        <EvolutionCard
          title={t('report:playerReport.durationOverTime')}
          subtitle={t('report:playerReport.durationOverTimeSubtitle')}
          icon={<Timer className="h-5 w-5 text-white" />}
          points={durationPoints}
          accent="violet"
          currentLabel={durationCurrent}
          trend={computeTrend(durationPoints.map((p) => p.y))}
          yFormatter={(v) => `${Math.round(v)}`}
          xFormatter={xFormatter}
          emptyText={t('report:playerReport.noDatedSessions')}
          delay={400}
        />
      </div>

      <div
        className={`grid grid-cols-1 gap-4 ${
          data.shots_breakdown_total ? 'lg:grid-cols-2' : ''
        }`}
      >
        <CourtZoneCard courtZone={data.court_zone_average} delay={500} />
        {data.shots_breakdown_total && (
          <CrossShotDistributionCard
            shotsBreakdown={data.shots_breakdown_total}
            delay={600}
          />
        )}
      </div>
    </div>
  );
};

function sortedByDate(sessions: PerSessionEntry[]): PerSessionEntry[] {
  return [...sessions].sort((a, b) => {
    const ta = a.played_at ? Date.parse(a.played_at) : 0;
    const tb = b.played_at ? Date.parse(b.played_at) : 0;
    return ta - tb;
  });
}

function formatDistance(meters: number): string {
  return meters >= 1000
    ? `${(meters / 1000).toFixed(2)} km`
    : `${Math.round(meters)} m`;
}

export default PlayerReportPage;
